<template>
  <v-layout>
    <v-container fluid>
      <!-- Modals -->
      <ScenarioAddEditDialog
        ref="scenarioAddEditDialog"
        @addScenarioFinish="addScenarioFinish"
      />

      <ScenarioCloneDialog
        :activeItem="activeItem"
        :tree="trees.scenarios"
        :filters="filters"
        ref="scenarioCloneDialog"
        @addScenarioFinish="addScenarioFinish"
      />

      <AdBreadcrumbs :items="breadcrumbs"/>
      <PanelLoading :loading="!ready"/>
      <v-row v-if="ready" class="d-flex flex-row flex-md-nowrap">
        <v-col
          class="flex-grow-0 col-treeview"
          cols="12"
          md="auto"
        >
          <TreeviewPanel
            :filters="filters"
            :trees="trees"
            :active-item.sync="activeItem"
            :active-tree.sync="activeTree"
            :disable-animation="!ready"
            indicateNoData
          >
            <!-- Dataset selection -->
            <template v-slot:tree.scenarios.before-search>
              <v-card-text class="pb-0 slot-tree-before-search">
                <v-autocomplete
                  v-model="selectedDataset"
                  :items="datasetItems"
                  :label="$t('dataset')"
                  @change="loadScenarioItemsForDataset"
                  return-object
                  clearable
                  outlined
                  dense
                  hide-details
                  item-value="uuid"
                  single-line
                  :loading="$store.state.dataset.loading.datasetItems"
                >
                  <template #item="{ item }">
                    <AdStatusDot larger tooltip class="mr-3" :status="item.statusCode" icon="mdi-book-open-blank-variant"/>
                    {{ $filters.getMatchingTranslation({ value: item.name }) }}
                    ({{ item.versionLabel || $filters.formatDateShort(item.effectiveDate) }})
                  </template>
                  <template #selection="{ item }">
                    <div class="text-truncate">
                      <AdStatusDot class="mr-2" tooltip :status="item.statusCode" icon="mdi-book-open-blank-variant"/>
                      {{ $filters.getMatchingTranslation({ value: item.name }) }}
                      ({{ item.versionLabel || $filters.formatDateShort(item.effectiveDate) }})
                    </div>
                  </template>
                </v-autocomplete>
              </v-card-text>
            </template>

            <template v-slot:action-buttons>
              <div>
                <AdButton
                  v-if="userIsProjectAuthor && activeTree === 'scenarios' && serverFeature({ path: 'decor.scenarios', crudAction: 'write' })"
                  type="add"
                  :hint="$t('add-scenario')"
                  list-item
                  @click="addScenario"
                />
                <AdButton
                  v-if="userIsProjectAuthor && activeTree === 'scenarios' && noOfArtefactsFound > 0 && serverFeature({ path: 'decor.scenarios', crudAction: 'write' })"
                  type="clone"
                  :hint="$t('clone-scenario')"
                  list-item
                  @click="$refs.scenarioCloneDialog.open()"
                />
              </div>
            </template>
          </TreeviewPanel>
        </v-col>

        <v-col
          class="flex-grow-1 flex-shrink-1"
          cols="12"
          md="auto"
        >
          <router-view
            :key="`loadingActiveItem-${$store.state.scenario.scenarioItem ? $store.state.scenario.scenarioItem.id : ''}`"/>
          <v-card v-if="$route.name === 'scenarios'">
            <AdInfoOwl v-if="noOfArtefactsFound > 0">
              {{ $t('select-scenario') }}
            </AdInfoOwl>
            <AdInfoOwl v-else>
              {{ $t('no-scenarios-yet') }}
              <span v-if="userIsProjectAuthor && activeTree === 'scenarios'" v-html="$t('use-kebab-to-add')"/>
            </AdInfoOwl>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import ScenarioAddEditDialog from '@/components/Scenario/ScenarioAddEditDialog'
import ScenarioCloneDialog from '@/components/Scenario/ScenarioCloneDialog'
import TreeviewPanel from '@/components/Utilities/Treeview/TreeviewPanel'
import ScenarioService from '@/services/ScenarioService'

export default {
  name: 'ScenarioPanel',

  components: {
    TreeviewPanel,
    ScenarioCloneDialog,
    ScenarioAddEditDialog
  },

  data () {
    return {
      ready: false,
      activeTree: null,
      ignoreActiveTreeSwitch: false,
      selectedDataset: null,
      selectedItem: null,
      filters: [],
      scenariosForDataset: null,
      loadingScenariosForDataset: false
    }
  },

  computed: {
    ...mapGetters('server', ['getItemStatuses']),
    ...mapState({
      scenarioItems: (state) => state.scenario.scenarioItems,
      scenarioItem: (state) => state.scenario.scenarioItem,
      actorItems: (state) => state.scenario.actors
    }),

    /**
     * Active item.
     */
    activeItem: {
      // Getter.
      get () {
        // Read from state.
        return this.$store.state.scenario.activeItem
      },
      // Setter.
      set (activeItem) {
        // Commit set active item mutation.
        this.$store.commit('scenario/setActiveItem', activeItem)
      }
    },
    breadcrumbs () {
      const breadcrumbs = [
        {
          text: this.$filters.getMatchingTranslation({ value: this.projectInFocus?.name }),
          to: { name: 'project-overview' },
          show: ['scenarios', 'scenarios-details', 'scenarios-details-concepts']
        },
        {
          text: this.$t('scenarios'),
          disabled: true,
          show: ['scenarios', 'scenarios-details', 'scenarios-details-concepts']
        },
        {
          exact: true,
          text: this.$t('scenarios'),
          to: { name: 'scenarios' },
          show: ['scenarios', 'scenarios-details', 'scenarios-details-concepts']
        },
        {
          text: this.$filters.getMatchingTranslation({
            value: this.$store.state.scenario.activeItem?.name
          }) || this.$route.params?.id,
          to: {
            name: 'scenarios-details',
            params: {
              scenarioId: this.$route.params?.scenarioId,
              scenarioVersion: this.$route.params?.scenarioVersion
            }
          },
          show: ['scenarios-details', 'scenarios-details-concepts']
        },
        {
          text: this.$route.params?.scenarioVersion ? this.$filters.formatDateShort(this.$route.params?.scenarioVersion) : '',
          to: {
            name: 'scenarios-details',
            params: {
              scenarioId: this.$route.params?.scenarioId,
              scenarioVersion: this.$route.params?.scenarioVersion
            }
          },
          show: ['scenarios-details', 'scenarios-details-concepts']
        },
        {
          text: this.$filters.getMatchingTranslation({
            value: this.$store.state.scenario.activeConceptItem?.name
          }) || this.$route.params?.conceptId,
          to: {
            name: 'scenarios-details-concepts',
            params: {
              scenarioId: this.$route.params?.scenarioId,
              scenarioVersion: this.$route.params?.scenarioVersion,
              conceptId: this.$route.params?.conceptId,
              conceptVersion: this.$route.params?.conceptVersion
            }
          },
          show: ['scenarios-details-concepts']
        },
        {
          text: this.$route.params?.conceptVersion ? this.$filters.formatDateShort(this.$route.params?.conceptVersion) : '',
          to: {
            name: 'scenarios-details-concepts',
            params: {
              scenarioId: this.$route.params?.scenarioId,
              scenarioVersion: this.$route.params?.scenarioVersion,
              conceptId: this.$route.params?.conceptId,
              conceptVersion: this.$route.params?.conceptVersion
            }
          },
          show: ['scenarios-details-concepts']
        }
      ]
      return breadcrumbs.filter((breadcrumb) => breadcrumb.show.includes(this.$route.name))
    },

    datasetItems () {
      return this.$store.getters['dataset/datasetItems'] || []
    },
    scenarioItemsForTree () {
      const items = this.selectedDataset
        ? this.scenariosForDataset
        : this.$store.state.scenario.scenarioItems

      return items || []
    },

    loadingScenarioItems () {
      if (this.selectedDataset) {
        return this.loadingScenariosForDataset
      }
      return this.$store.state.scenario.loading.scenarioItems
    },

    /**
     * Contains tree structure.
     * @returns {{}}
     */
    trees () {
      return {
        scenarios: {
          artefact: 'SC',
          name: this.$t('scenarios'),
          icon: 'mdi-movie',
          loading: this.loadingScenarioItems,
          loaded: this.$store.state.scenario.loaded.scenarioItems,
          items: this.scenarioItemsForTree,
          menu: this.userIsProjectAuthor,
          insertable: (item) => this.serverFeature({ path: 'decor.scenarios.transaction', crudAction: 'write' }) && this.userIsEditor && ['draft', 'new'].includes(item.statusCode),
          editable: false,
          expandable: true,
          key: 'id',
          text: (item, returnObject) => this.$filters.getMatchingTranslation({
            value: (item?.name || []).map((name) => ({
              ...name,
              '#text': item?.versionLabel
                ? `${name?.['#text']} (${item?.versionLabel})`
                : name?.['#text']
            })),
            selector: '#text',
            returnObject
          }),
          children: 'transaction',
          onSelect: (activeItem) => {
            // Check if ready is true.
            if (this.ready === true) {
              // Set scenario item.
              this.$store.commit('scenario/setScenarioItem', {
                ...activeItem,
                name: activeItem.name.map((name) => ({
                  ...name,
                  '#text': name?.['#textOriginal'] || name?.['#text']
                }))
              })

              // Only change active item has changed.
              if (
                this.$route.params?.scenarioId !== activeItem?.id ||
                this.$route.params?.scenarioVersion !== activeItem?.effectiveDate
              ) {
                // Push router url.
                this.$router.push({
                  name: 'scenarios-details',
                  params: {
                    scenarioId: activeItem?.id,
                    scenarioVersion: activeItem?.effectiveDate
                  }
                }).catch(() => {
                  // Do nothing.
                })
              }
            }
          },
          onInsert: ({ type, mode, item, target }) => {
            // Define scenario.
            let scenario = this.trees.scenarios.items.find((i) => i.id === item.id)
            // Check if scenario is still not found.
            if (!scenario) {
              // Get matching scenario.
              scenario = !!item?.type && item?.type !== 'group'
                ? this.trees.scenarios.items.find((i) => !!i.transaction.filter((t) => t.id === target.parent.id).length)
                : this.trees.scenarios.items.find((i) => i.id === target.parent.id)
            }

            // Open and edit scenario dialog.
            this.$refs.scenarioAddEditDialog.openAndEdit({ type, mode, item, target, scenario })
          },
          onBeforeEdit: () => {
            // Do nothing.
          },
          onAfterEdit: () => {
            // Do nothing.
          },
          onEdit: () => {
            // Do nothing.
          }
        }
      }
    },

    /**
     * No of artefacts (sceanrios) found in project
     */
    noOfArtefactsFound () {
      return this.trees.scenarios.items?.length
    }
  },

  watch: {
    /**
     * Watch item status codes.
     */
    getItemStatuses: {
      immediate: true,
      deep: true,
      handler (getItemStatuses) {
        // Define filters.
        const filters = []
        // Define default actives.
        const defaultActive = ['draft', 'pending', 'final', 'new']
        // Loop each item status code.
        for (const itemStatusCode of getItemStatuses) {
          // Push new filter.
          filters.push({
            name: itemStatusCode.label,
            key: itemStatusCode.value,
            active: defaultActive.includes(itemStatusCode.value)
          })
        }

        // Apply filters.
        this.filters = filters
      }
    }
  },

  created () {
    // Load scenario items.
    this.loadScenarioItems().catch((error) => this.$confirm(this.$t(error.message)))

    if (!this.$store.state.dataset.loaded.datasetItems) {
      this.$store.dispatch('dataset/loadDatasetItems')
    }

    if (!this.$store.state.scenario.loaded.actors) {
      this.$store.dispatch('scenario/setActors')
        .catch(error => {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        })
    }
  },

  methods: {
    /**
     * Load scenario items.
     */
    async loadScenarioItems () {
      // Get scenario items from service via vuex store.
      const scenarioItems = this.$store.state.scenario.loaded.scenarioItems
        ? this.$store.state.scenario.scenarioItems
        : await this.$store.dispatch('scenario/loadScenarioItems').catch((error) => {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        })
      // Return new promise.
      return new Promise((resolve, reject) => {
        // Get route params.
        const { scenarioId, scenarioVersion } = this.$route.params

        // Get matching scenario.
        const matchingScenario = this.$filters.listHelper.findItemInList({
          items: scenarioItems,
          children: 'transaction',
          handler: (match) => match.id === scenarioId && match.effectiveDate === scenarioVersion
        })

        // Check if matching scenario is nil.
        if (!matchingScenario) {
          // Set ready to true.
          this.ready = true
          // Return and stop execution.
          return resolve(null)
        }

        // Set scenario item
        this.$store.commit('scenario/setScenarioItem', matchingScenario)
        // Generate active scenario item.
        this.activeItem = { id: scenarioId, effectiveDate: scenarioVersion }
        // Set ready to true.
        this.ready = true

        // Resolve matching scenario.
        return resolve(matchingScenario)
      })
    },

    loadScenarioItemsForDataset () {
      if (!this.selectedDataset) {
        this.scenariosForDataset = null
        return
      }
      const { id: datasetId, effectiveDate: datasetEffectiveDate } = this.selectedDataset || {}
      this.loadingScenariosForDataset = true
      ScenarioService.getScenarioList({ prefix: this.projectInFocus?.prefix, datasetId, datasetEffectiveDate })
        .then(res => {
          this.scenariosForDataset = res.data.scenario || null
        })
        .catch(error => {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        })
        .finally(() => {
          this.loadingScenariosForDataset = false
        })
    },

    async addScenario () {
      if (!this.actorItems.length) {
        return this.$oops(this.$t('add-actor-first'))
      }

      this.$refs.scenarioAddEditDialog.openAndAdd()
    },

    /**
     * Add scenario finish event.
     * @param activeItem
     * @returns {Promise<void>}
     */
    async addScenarioFinish (activeItem) {
      // If the activeItem is parsed, the user wants to open the newly added scenario.
      if (activeItem) {
        // Set active item.
        this.activeItem = { id: activeItem?.id, effectiveDate: activeItem.effectiveDate }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .treeview-panel-item-not-yet-used {
  border-left: 8px solid #dbe8d3;
  padding-left: 24px;

  .v-treeview {
    padding-left: 0;
  }
}
</style>
